import { useEffect, useRef, useState } from 'react';
import Select, { createFilter } from 'react-select';
import { NODE_PRESENTATION, OPTION_LABELS } from './nodePresentationRegistry';
import { flowEdgeToBackend, flowNodeToBackend, toDisplayText } from './model';
import { analyzeNodeSemantics } from './projectResults';

const optionsFor = (field, catalog) => field.type === 'catalog' ? (catalog?.[field.catalog] || []) : (field.options || []);
const isSource = type => type === 'source.basic_parameter' || type === 'source.legacy_calculated_parameter';
const deviceLabel = device => toDisplayText(device?.name ?? device?.serial_number ?? device?.mac, 'Dispositivo sin nombre');
const branchId = branch => branch?.id_branch_offices ?? branch?.branch_id ?? branch?.id_company ?? branch?.id;
const branchLabel = branch => toDisplayText(branch?.office_name ?? branch?.name ?? branch?.company_name, 'Sucursal sin nombre');
const basicParameterId = parameter => parameter?.idbasic_parameters ?? parameter?.id;
const calculatedParameterId = parameter => parameter?.device_calculated_parameter?.id ?? parameter?.id;
const parameterLabel = parameter => toDisplayText(parameter?.name ?? parameter?.name_override ?? parameter?.tag, 'Parámetro sin nombre');
const selectFilter = createFilter({ ignoreAccents: true, ignoreCase: true });

const periodReducerExplanation = {
  sum: 'Suma todos los resultados parciales. Es la opción habitual para consumos, energía y cantidades acumuladas.',
  avg: 'Representa el comportamiento promedio durante el período.',
  max: 'Conserva el valor más alto alcanzado durante el período.',
  min: 'Conserva el valor más bajo alcanzado durante el período.',
  last: 'Conserva el valor más reciente del período.',
};

const recommendedPeriodReducer = (node, nodes, edges) => {
  if (node?.data?.nodeType !== 'output.number') return null;
  const graph = {
    nodes: (nodes || []).map(flowNodeToBackend),
    edges: (edges || []).map(flowEdgeToBackend),
  };
  const output = graph.nodes.find(candidate => candidate.node_key === node.id);
  return analyzeNodeSemantics(graph, output).additive ? 'sum' : null;
};

export default function NodeInspector({ node, nodes, edges, catalog, sourceCatalogs, sourceScope, onChange, onDelete, disabled }) {
  const [branches, setBranches] = useState([]);
  const [devices, setDevices] = useState([]);
  const [parameters, setParameters] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loadingParameters, setLoadingParameters] = useState(false);
  const [branchError, setBranchError] = useState('');
  const [deviceError, setDeviceError] = useState('');
  const [parameterError, setParameterError] = useState('');
  const activeNodeId = useRef(null);
  const nodeType = node?.data?.nodeType;
  const deviceId = node?.data?.config?.device_id;
  const reducerRecommendation = recommendedPeriodReducer(node, nodes, edges);

  useEffect(() => {
    if (disabled || !node || nodeType !== 'output.number' || node.data.config?.period_reducer || !reducerRecommendation) return;
    onChange(node.id, {
      config: { ...(node.data.config || {}), period_reducer: reducerRecommendation },
    });
  }, [disabled, node, nodeType, onChange, reducerRecommendation]);

  useEffect(() => {
    let cancelled = false;
    if (!isSource(nodeType) || !sourceCatalogs) return undefined;
    setLoadingBranches(true); setBranchError('');
    sourceCatalogs.branches()
      .then(rows => { if (!cancelled) setBranches(Array.isArray(rows) ? rows : []); })
      .catch(() => {
        if (!cancelled) { setBranches([]); setBranchError('No se pudieron cargar las sucursales.'); }
      })
      .finally(() => { if (!cancelled) setLoadingBranches(false); });
    setLoadingDevices(true); setDeviceError('');
    sourceCatalogs.devices()
      .then(rows => { if (!cancelled) setDevices(Array.isArray(rows) ? rows : []); })
      .catch(() => {
        if (!cancelled) { setDevices([]); setDeviceError('No se pudieron cargar los dispositivos.'); }
      })
      .finally(() => { if (!cancelled) setLoadingDevices(false); });
    return () => { cancelled = true; };
  }, [nodeType, sourceCatalogs]);

  useEffect(() => {
    if (!isSource(nodeType)) return;
    const nodeChanged = activeNodeId.current !== node?.id;
    activeNodeId.current = node?.id;
    if (nodeChanged) setSelectedBranchId(sourceScope?.branchId == null ? null : Number(sourceScope.branchId));
    if (!deviceId) {
      return;
    }
    const selectedDevice = devices.find(device => Number(device.id) === Number(deviceId));
    if (selectedDevice?.branch_fk != null) setSelectedBranchId(Number(selectedDevice.branch_fk));
  }, [deviceId, devices, node?.id, nodeType, sourceScope?.branchId]);

  useEffect(() => {
    let cancelled = false;
    setParameters([]);
    if (!isSource(nodeType) || !deviceId || !sourceCatalogs) {
      setLoadingParameters(false);
      setParameterError('');
      return undefined;
    }
    setLoadingParameters(true); setParameterError('');
    const request = nodeType === 'source.basic_parameter'
      ? sourceCatalogs.basicParameters(deviceId)
      : sourceCatalogs.calculatedParameters(deviceId);
    request
      .then(rows => { if (!cancelled) setParameters(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) { setParameters([]); setParameterError('No se pudieron cargar los parámetros del dispositivo.'); } })
      .finally(() => { if (!cancelled) setLoadingParameters(false); });
    return () => { cancelled = true; };
  }, [deviceId, nodeType, sourceCatalogs]);

  if (!node) return <aside className="cm-panel cm-inspector cm-inspector-empty">
    <div><h2>Configuración</h2><p>Seleccioná un nodo para editar su nombre y propiedades.</p></div>
  </aside>;

  const presentation = NODE_PRESENTATION[node.data.nodeType] || { label: node.data.nodeType, fields: [] };
  const parameterField = nodeType === 'source.basic_parameter' ? 'parameter_id' : 'device_calculated_parameter_id';
  const getParameterId = nodeType === 'source.basic_parameter' ? basicParameterId : calculatedParameterId;
  const updateConfig = (field, rawValue) => {
    const config = { ...(node.data.config || {}) };
    if (rawValue === '') delete config[field.name];
    else config[field.name] = field.type === 'number' ? Number(rawValue) : rawValue;
    onChange(node.id, { config });
  };
  const updateDevice = rawValue => {
    const config = { ...(node.data.config || {}) };
    if (rawValue === '') delete config.device_id;
    else config.device_id = Number(rawValue);
    delete config[parameterField];
    setParameters([]);
    onChange(node.id, { config });
  };
  const updateBranch = rawValue => {
    setSelectedBranchId(rawValue === '' ? null : Number(rawValue));
    const config = { ...(node.data.config || {}) };
    delete config.device_id;
    delete config[parameterField];
    setParameters([]);
    onChange(node.id, { config });
  };
  const inCompany = device => sourceScope?.companyId == null || Number(device.company_fk) === Number(sourceScope.companyId);
  const inProjectBranch = device => sourceScope?.branchId == null || Number(device.branch_fk) === Number(sourceScope.branchId);
  const scopedDevices = devices.filter(device => inCompany(device) && inProjectBranch(device));
  const catalogBranchOptions = branches
    .filter(branch => sourceScope?.companyId == null || Number(branch.parent_organization) === Number(sourceScope.companyId))
    .filter(branch => sourceScope?.branchId == null || Number(branchId(branch)) === Number(sourceScope.branchId))
    .map(branch => ({ value: Number(branchId(branch)), label: branchLabel(branch) }))
    .filter(option => Number.isFinite(option.value));
  const deviceBranchOptions = scopedDevices
    .filter(device => device?.branch_fk != null && device?.branch_office)
    .map(device => ({ value: Number(device.branch_fk), label: device.branch_office }))
    .filter((option, index, options) => Number.isFinite(option.value) && options.findIndex(item => item.value === option.value) === index);
  // Si el catálogo de sucursales falla, la relación incluida en los
  // dispositivos permite mantener operativo el selector jerárquico.
  const branchOptions = catalogBranchOptions.length ? catalogBranchOptions : deviceBranchOptions;
  const filteredDevices = scopedDevices.filter(device => (
    selectedBranchId != null && Number(device.branch_fk) === Number(selectedBranchId)
  ));
  const deviceOptions = filteredDevices.map(device => ({ value: Number(device.id), label: deviceLabel(device) }));
  const parameterOptions = parameters
    .map(parameter => ({ value: Number(getParameterId(parameter)), label: parameterLabel(parameter) }))
    .filter(option => Number.isFinite(option.value));
  const selectedBranchOption = branchOptions.find(option => option.value === Number(selectedBranchId)) || null;
  const selectedDeviceOption = deviceOptions.find(option => option.value === Number(deviceId)) || null;
  const selectedParameterOption = parameterOptions.find(option => option.value === Number(node.data.config?.[parameterField])) || null;
  const configuredDevice = devices.find(device => Number(device.id) === Number(deviceId));
  const sourceOutsideScope = Boolean(deviceId && configuredDevice && (!inCompany(configuredDevice) || !inProjectBranch(configuredDevice)));

  return <aside className="cm-panel cm-inspector" aria-label="Configuración del nodo">
    <div className="cm-panel-title"><h2>{presentation.label}</h2><p>{presentation.description}</p></div>
    <label>Nombre visible
      <input disabled={disabled} value={toDisplayText(node.data.name)} maxLength={255} placeholder={presentation.label} onChange={event => onChange(node.id, { name: event.target.value || null })} />
    </label>
    {isSource(nodeType) && <div className="cm-source-selectors">
      {sourceScope?.branchName && <p className="cm-catalog-message">Sucursal del proyecto: <strong>{sourceScope.branchName}</strong></p>}
      {sourceOutsideScope && <p className="cm-catalog-message is-error">La medición configurada pertenece a otra empresa o sucursal. Seleccioná una fuente válida antes de publicar.</p>}
      {sourceScope?.branchId == null && <label>Sucursal
        <Select
          className="cm-searchable-select"
          classNamePrefix="cm-select"
          value={selectedBranchOption}
          options={branchOptions}
          onChange={option => updateBranch(option?.value ?? '')}
          isClearable
          isSearchable
          filterOption={selectFilter}
          isDisabled={disabled || loadingBranches}
          placeholder={loadingBranches ? 'Cargando sucursales…' : 'Seleccioná una sucursal'}
          noOptionsMessage={() => loadingBranches ? 'Cargando sucursales…' : 'No hay sucursales disponibles'}
        />
      </label>}
      <label>Dispositivo
        <Select
          className="cm-searchable-select"
          classNamePrefix="cm-select"
          value={selectedDeviceOption}
          options={deviceOptions}
          onChange={option => updateDevice(option?.value ?? '')}
          isClearable
          isSearchable
          filterOption={selectFilter}
          isDisabled={disabled || loadingDevices || selectedBranchId == null}
          placeholder={loadingDevices ? 'Cargando dispositivos…' : selectedBranchId == null ? 'Seleccioná primero una sucursal' : 'Seleccioná un dispositivo'}
          noOptionsMessage={() => loadingDevices ? 'Cargando dispositivos…' : selectedBranchId == null ? 'Seleccioná primero una sucursal' : 'La sucursal no tiene dispositivos disponibles'}
        />
      </label>
      <label>{nodeType === 'source.basic_parameter' ? 'Parámetro' : 'Parámetro calculado'}
        <Select
          className="cm-searchable-select"
          classNamePrefix="cm-select"
          value={selectedParameterOption}
          options={parameterOptions}
          onChange={option => updateConfig({ name: parameterField, type: 'number' }, option?.value ?? '')}
          isClearable
          isSearchable
          filterOption={selectFilter}
          isDisabled={disabled || !deviceId || loadingParameters}
          placeholder={loadingParameters ? 'Cargando parámetros…' : !deviceId ? 'Seleccioná primero un dispositivo' : 'Seleccioná un parámetro'}
          noOptionsMessage={() => loadingParameters ? 'Cargando parámetros…' : !deviceId ? 'Seleccioná primero un dispositivo' : 'El dispositivo no tiene parámetros disponibles'}
        />
      </label>
      {!loadingBranches && !branchOptions.length && !branchError && <p className="cm-catalog-message">No hay sucursales disponibles.</p>}
      {selectedBranchId != null && !loadingDevices && !filteredDevices.length && !deviceError && <p className="cm-catalog-message">La sucursal seleccionada no tiene dispositivos disponibles.</p>}
      {!!deviceId && !loadingParameters && !parameters.length && !parameterError && <p className="cm-catalog-message">El dispositivo seleccionado no tiene parámetros disponibles.</p>}
      {branchError && <p className="cm-catalog-message is-error">{branchError}</p>}
      {deviceError && <p className="cm-catalog-message is-error">{deviceError}</p>}
      {parameterError && <p className="cm-catalog-message is-error">{parameterError}</p>}
    </div>}
    {(presentation.fields || []).map(field => {
      const currentValue = node.data.config?.[field.name] ?? '';
      const isRecommendedReducer = field.name === 'period_reducer' && reducerRecommendation === currentValue;
      return <label key={field.name}>{field.label}{!field.optional && <span className="cm-required"> *</span>}
      {field.type === 'select' || field.type === 'catalog'
        ? <select disabled={disabled} value={currentValue} onChange={event => updateConfig(field, event.target.value)}>
          <option value="">{field.emptyLabel || 'Sin configurar'}</option>
          {optionsFor(field, catalog).map(option => <option key={option} value={option}>{OPTION_LABELS[`${field.optionLabelPrefix || ''}${option}`] || OPTION_LABELS[option] || option}{field.name === 'period_reducer' && reducerRecommendation === option ? ' (recomendado)' : ''}</option>)}
        </select>
        : <input disabled={disabled} type={field.type} min={field.min} value={node.data.config?.[field.name] ?? ''} placeholder={field.placeholder} onChange={event => updateConfig(field, event.target.value)} />}
      {isRecommendedReducer && <span className="cm-recommendation">Recomendado para este modelo</span>}
      {field.name === 'period_reducer' && currentValue && <small className="cm-inspector-help">{periodReducerExplanation[currentValue]}</small>}
      {field.help && <small className="cm-inspector-help">{field.help}</small>}
    </label>})}
    {!(presentation.fields || []).length && <p className="cm-inspector-note">Este nodo no requiere configuración adicional. Conectá sus puertos en el canvas.</p>}
    {!disabled && <button type="button" className="cm-danger" onClick={() => onDelete(node.id)}>Eliminar nodo</button>}
  </aside>;
}
